import { useEffect, useMemo, useState } from 'react';
import type { FieldValue, GeneratorConfigV1 } from '../../types/generator-config';
import { defaultValueForField, initializeAnswers, stepValidationErrors } from '../../lib/fieldState';
import { visibleFields, visibleSteps, type Answers } from '../../lib/visibility';
import { computePrice, formatPrice } from '../../lib/pricing';
import { RenderedField } from './RenderedField';
import { SummaryScreen } from './SummaryScreen';

interface GeneratorRendererProps {
    config: GeneratorConfigV1;
    /** Si true, affiche un bouton "Reset" dans le header */
    showResetButton?: boolean;
    /** Callback optionnel quand l'utilisateur termine le formulaire */
    onComplete?: (answers: Answers, total: number) => void;
    /**
     * Si fournis, les submissions seront réellement envoyées en DB depuis l'écran
     * de résumé (via `useSubmitLead`). Sinon → mode aperçu (éditeur), pas d'insert.
     */
    generatorId?: string;
    organizationId?: string;
    /**
     * Permet de forcer l'état d'affichage du preview depuis l'éditeur.
     * 'summary' → saute directement à l'écran de résumé (et donc au formulaire
     * de captation de leads s'il est activé).
     */
    previewTarget?: 'form' | 'summary';
}

const LOCALE_LABEL: Record<string, string> = { fr: 'FR', en: 'EN' };

/**
 * Renderer interactif complet. Utilisé en preview dans l'éditeur,
 * et réutilisable tel quel pour la page publique des générateurs (phase 4).
 */
export function GeneratorRenderer({
    config,
    showResetButton = true,
    onComplete,
    generatorId,
    organizationId,
    previewTarget,
}: GeneratorRendererProps) {
    const { branding } = config;

    // Étapes visibles (peut changer avec les réponses)
    const [answers, setAnswers] = useState<Answers>(() => initializeAnswers(config));
    const [stepIdx, setStepIdx] = useState(0);
    const [errors, setErrors] = useState<Set<string>>(new Set());
    const [completed, setCompleted] = useState(false);

    const steps = useMemo(() => visibleSteps(config.steps, answers), [config.steps, answers]);
    const activeStep = steps[stepIdx];
    const isLastStep = stepIdx === steps.length - 1;

    // Quand la config change (édition live), on garde les réponses existantes
    // et on ajoute juste un défaut pour les nouveaux fields.
    useEffect(() => {
        setAnswers((prev) => {
            const next: Answers = { ...prev };
            for (const step of config.steps) {
                for (const field of step.fields) {
                    if (!(field.id in next)) {
                        next[field.id] = defaultValueForField(field);
                    }
                }
            }
            return next;
        });
    }, [config]);

    // Clamp du step index si la config change
    useEffect(() => {
        if (steps.length === 0) {
            setStepIdx(0);
            return;
        }
        if (stepIdx > steps.length - 1) {
            setStepIdx(steps.length - 1);
        }
    }, [steps.length, stepIdx]);

    // Synchronise l'état avec la cible demandée par l'éditeur (ex: onglet Leads).
    useEffect(() => {
        if (previewTarget === 'summary') {
            setCompleted(true);
            setErrors(new Set());
        } else if (previewTarget === 'form') {
            setCompleted(false);
        }
    }, [previewTarget]);

    const price = useMemo(() => computePrice(config, answers), [config, answers]);

    const setAnswer = (fieldId: string, value: FieldValue) => {
        setAnswers((prev) => ({ ...prev, [fieldId]: value }));
        if (errors.has(fieldId)) {
            const next = new Set(errors);
            next.delete(fieldId);
            setErrors(next);
        }
    };

    const handleNext = () => {
        if (!activeStep) return;
        const invalidIds = stepValidationErrors(activeStep.fields, answers);
        if (invalidIds.length > 0) {
            setErrors(new Set(invalidIds));
            return;
        }
        setErrors(new Set());
        if (isLastStep) {
            setCompleted(true);
            onComplete?.(answers, price.total);
        } else {
            setStepIdx((v) => v + 1);
        }
    };

    const handleBack = () => {
        setErrors(new Set());
        setStepIdx((v) => Math.max(0, v - 1));
    };

    const handleReset = () => {
        setAnswers(initializeAnswers(config));
        setStepIdx(0);
        setErrors(new Set());
        setCompleted(false);
    };

    const jumpToStep = (i: number) => {
        if (completed) {
            setCompleted(false);
            setStepIdx(i);
            return;
        }
        if (i <= stepIdx) {
            setStepIdx(i);
            setErrors(new Set());
        }
    };

    return (
        <div
            className="r-module"
            style={{ '--preview-accent': branding.primaryColor } as React.CSSProperties}
        >
            <header className="preview-module-head">
                <div className="preview-module-brand">
                    {branding.logoUrl && (
                        <img src={branding.logoUrl} alt="" className="preview-module-logo" />
                    )}
                    <div className="preview-module-identity">
                        <div className="preview-module-name">
                            {branding.companyName || 'Votre entreprise'}
                            <span className="preview-module-sep" aria-hidden>·</span>
                            <span className="preview-module-tagline">Estimateur de projet</span>
                        </div>
                        <div className="preview-module-sub">
                            {LOCALE_LABEL[branding.locale]} · {branding.currency}
                        </div>
                    </div>
                    {showResetButton && (answers && Object.keys(answers).length > 0) && (
                        <button
                            type="button"
                            className="r-reset-btn"
                            onClick={handleReset}
                            title="Recommencer"
                            aria-label="Recommencer"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                            </svg>
                        </button>
                    )}
                </div>

                {steps.length > 0 && (
                    <div className="preview-stepper" role="list">
                        {steps.map((s, i) => (
                            <button
                                key={s.id}
                                type="button"
                                role="listitem"
                                className={`preview-stepper-dot${!completed && i === stepIdx ? ' is-active' : ''}${(!completed && i < stepIdx) || completed ? ' is-done' : ''}`}
                                onClick={() => jumpToStep(i)}
                                aria-label={`Étape ${i + 1} : ${s.title}`}
                                title={s.title}
                            >
                                <span>{i + 1}</span>
                            </button>
                        ))}
                    </div>
                )}
            </header>

            <div className="preview-module-body">
                {completed ? (
                    <SummaryScreen
                        config={config}
                        answers={answers}
                        onRestart={handleReset}
                        onEdit={(i) => {
                            setCompleted(false);
                            setStepIdx(i);
                        }}
                        generatorId={generatorId}
                        organizationId={organizationId}
                    />
                ) : !activeStep ? (
                    <div className="preview-empty">
                        <h3>Aucune étape configurée</h3>
                        <p>Ajoutez une étape via le panneau de gauche pour commencer.</p>
                    </div>
                ) : (
                    <>
                        <div className="preview-step-header">
                            <span className="preview-step-eyebrow">
                                Étape {stepIdx + 1} / {steps.length}
                            </span>
                            <h3>{activeStep.title || <em>Titre manquant</em>}</h3>
                            {activeStep.description && (
                                <p className="preview-step-desc">{activeStep.description}</p>
                            )}
                        </div>

                        {visibleFields(activeStep.fields, answers).length === 0 ? (
                            <div className="preview-step-empty">
                                Aucun champ dans cette étape.
                            </div>
                        ) : (
                            <div className="preview-fields">
                                {visibleFields(activeStep.fields, answers).map((f) => (
                                    <RenderedField
                                        key={f.id}
                                        field={f}
                                        value={answers[f.id] ?? null}
                                        invalid={errors.has(f.id)}
                                        onChange={(v) => setAnswer(f.id, v)}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="preview-step-nav">
                            <button
                                type="button"
                                className="preview-btn-ghost"
                                onClick={handleBack}
                                disabled={stepIdx === 0}
                            >
                                Précédent
                            </button>
                            <button
                                type="button"
                                className="preview-btn-primary"
                                onClick={handleNext}
                            >
                                {isLastStep ? 'Voir mon estimation' : 'Suivant'}
                            </button>
                        </div>
                    </>
                )}
            </div>

            <footer className="preview-module-foot">
                <div>
                    <span className="preview-foot-label">
                        {completed ? 'Total estimé' : 'Estimation en cours'}
                    </span>
                    <span className="preview-foot-amount">
                        {formatPrice(price.total, price.currency)}
                    </span>
                </div>
                <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="preview-module-powered"
                    aria-label="Propulsé par Quotio — visiter quotio.io"
                    title="Propulsé par Quotio"
                >
                    <span className="preview-module-powered-label">Propulsé par</span>
                    <img src="/logoQuotio.png" alt="Quotio" className="preview-module-powered-logo" />
                </a>
            </footer>
        </div>
    );
}
